/**
 * Toast — lightweight notification that slides in from the top-right.
 *
 * Usage:
 *   Toast.show('Saved!');                     // success (default)
 *   Toast.show('Something went wrong', 'error');
 *   Toast.show('Tip: you can drag columns', 'info');
 */

const TOAST_DURATION = 3000;   // auto-dismiss after 3 s
const ANIMATION_MS   = 300;    // matches CSS transition duration

/** Ensure the fixed container exists (created once, reused). */
function getContainer() {
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.className = 'toast-container';
    document.body.appendChild(container);
  }
  return container;
}

export class Toast {
  /**
   * @param {string} message
   * @param {'success' | 'error' | 'info'} [type='success']
   */
  static show(message, type = 'success', options = {}) {
    const container = getContainer();

    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;

    // Icon per type
    const icons = {
      success: `<svg class="toast-icon" viewBox="0 0 20 20" fill="currentColor" width="18" height="18"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>`,
      error:   `<svg class="toast-icon" viewBox="0 0 20 20" fill="currentColor" width="18" height="18"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>`,
      info:    `<svg class="toast-icon" viewBox="0 0 20 20" fill="currentColor" width="18" height="18"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>`,
    };

    toast.innerHTML = `
      ${icons[type] || icons.info}
      <span class="toast-message"></span>
    `;

    toast.querySelector('.toast-message').textContent = message;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    if (options.onAction) {
      const action = document.createElement('button');
      action.textContent = options.actionLabel || 'Undo';
      action.addEventListener('click', async event => {
        event.stopPropagation();
        action.disabled = true;
        clearTimeout(timer);
        try { await options.onAction(); dismiss(); }
        catch (error) { action.disabled = false; Toast.show(error.message || 'Try again.', 'error'); }
      });
      toast.append(action);
    }
    container.appendChild(toast);

    // Trigger entrance animation on next frame
    requestAnimationFrame(() => {
      toast.classList.add('toast--visible');
    });

    // Auto-dismiss
    const timer = setTimeout(() => dismiss(), options.duration ?? TOAST_DURATION);

    // Allow click to dismiss early
    toast.addEventListener('click', () => {
      clearTimeout(timer);
      dismiss();
    });

    function dismiss() {
      toast.classList.remove('toast--visible');
      toast.classList.add('toast--exit');
      setTimeout(() => toast.remove(), ANIMATION_MS);
    }
  }
}
